import Modernizr from 'core-libs/modernizr/modernizr';
import {appViews} from '@netx/core-app/singletons/appViews';
import {fsViews} from '@netx/core-app/singletons/fsViews';
import {currentPortal} from '@netx/core-cms/singletons/currentPortal';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {setGlobal} from '@netx/core-next/utils/setGlobal';
import {ready} from '@netx/core-next/whenReady';
import CoreModel from '@netx/core/lib/netx-model';
import CoreView from '@netx/core/lib/netx-view';
import {randomChars} from '@netx/core/utils/randomChars';
import customConfig from './config';
import LoadingViewTemplate from './templates/loadingView.html';
import PassiveAlertView from './views/passiveAlertView';

export {default} from 'netxcore@netx/core-app/controller';

/**
 * @event module:app#app:init
 */
dispatcher.once('app:init', function () {
	const $body = $(document.body);

	dispatcher.on('hash:changed', function (hash) {
		// if (!hash.includes('search/exact')) return;
		checkClasses();
	});

	const checkClasses = () => {
		const hash = window.location.hash;
		const slash = hash.indexOf('/');
		const base = hash.substr(1, (slash > -1 ? slash : hash.length) - 1);
		let remains = hash.substr((slash > -1 ? slash : 0) + 1);
		// We do not care about ids
		remains = remains.replace(/\d+/g, '');
		// Cehck we didn't leave any slashes at beginning or end after removing ids
		if (remains.substr(0, 1) == '/') {
			remains = remains.substr(1);
		}
		if (remains.substr(remains.length - 1, 1) == '/') {
			remains = remains.substr(0, remains.length - 1);
		}
		// Remove all classes we added ending in '-page'
		$body.removeClass((index, css) => {
			// We want to remove all classes matching '*-page'
			return (css.match(/\S+-page/g) || []).join(' ');
		});

		let bodyClasses = [];

		if (base) {
			// Get some specifics
			if (hash.includes('search/advanced')) {
				bodyClasses.push('search-advanced-page');
			} else if (hash.includes('search/basic')) {
				bodyClasses.push('search-basic-page');
			} else if (remains && (remains != base || slash > -1)) {
				remains = remains.replace(/\//g, '-');
				bodyClasses.push(`${base}-${remains}-page`);
			}
			// Standard based on hash base
			bodyClasses.push(`${base}-page`);
		}

		$body.addClass(bodyClasses.join(' '));
	};

	checkClasses();

	dispatcher.off('app:load:modules');
	dispatcher.on('app:load:modules', function (NS) {
		Promise.all([
			import(
				/* webpackMode: "eager"*/ /* webpackChunkName: "app-module-dependencies" */ '@netx/core-auth'
			),
			import(
				/* webpackMode: "eager"*/ /* webpackChunkName: "app-module-dependencies" */ '@netx/core-notification'
			),
		]).then((imports) => {
			const [Auth, Notification] = imports;
			dispatcher.trigger(
				'netx:load:module',
				[Auth.default, Notification.default],
				{
					initialized() {
						import(
							/* webpackMode: "eager"*/ /* webpackChunkName: "app-module-dependencies" */ '@netx/core-cms'
						).then((CMS) => {
							dispatcher.trigger('netx:load:module', [CMS.default], {
								initialized() {
									// **unfortunately** there are still some spots in portal that rely on this
									import(
										/* webpackMode: "eager"*/ /* webpackChunkName: "app-module-dependencies" */ '@netx/core-auto-template'
									).then((AutoTemplate) => {
										dispatcher.trigger(
											'netx:load:module',
											[AutoTemplate.default],
											{
												initialized() {
													dispatcher.trigger('app:extend', NS);
													dispatcher.trigger('app:initialized', NS);
												},
											},
										);
									});
								},
							});
						});
					},
				},
			);
		});
	});
});

/**
 * @event module:netx#netx:load:module:pdfViewer
 * @param {object} opts Options
 * @fires module:netx#netx:load:module
 */
dispatcher.on('netx:load:module:pdfViewer', function (opts, cb) {
	import(
		/* webpackChunkName: "@netx/core-pdf-viewer" */ '@netx/core-pdf-viewer'
	).then((PDFViewer) => {
		dispatcher.trigger('netx:load:deferred:module', PDFViewer.default, opts);
	});
});

/**
 * App Module Extensions
 * @event module:app#app:extend
 */
dispatcher.once('app:extend', function (NS) {
	////////////////////////////
	// Wrapper manipulation
	////////////////////////////

	dispatcher.on('routed', function () {
		// Kills things (like dropdowns and the like)
		NS.$body.click();
	});

	/**
	 * Displays a passive alert to the user
	 * - overriding core to hook up dismiss button, manage a body class,
	 *   and stop the slomo fade out when PASSIVE_ALERT_DELAY is high
	 * @event module:app#app:passiveAlert
	 * @param {object} options Options
	 */
	dispatcher.off('app:passiveAlert');
	dispatcher.on('app:passiveAlert', function (options) {
		const defaults = {
			className: 'alert alert-success',
			message: 'An event of limited importance has occurred.',
		};

		// create passive alert model if it's not here yet.
		if (!Netx.app.passiveAlert) {
			Netx.app.passiveAlert = new CoreModel(defaults);
		}

		// create/insert passive alert view if it's not here yet.
		if (!Netx.views.passiveAlertView) {
			const pav = (Netx.views.passiveAlertView = new PassiveAlertView({
				model: Netx.app.passiveAlert,
			}));
			$('body').append(pav.el);
		}

		Netx.app.passiveAlert.set(defaults);
		// set props of model to options (triggers render)
		if (_.isString(options)) {
			// just a string?
			Netx.app.passiveAlert.set({message: options});
		} else if (_.isObject(options)) {
			// a whole object?
			Netx.app.passiveAlert.set(options);
		}

		// show view
		Netx.views.passiveAlertView.$el.show(); // could be a fade in ...
		$('body').addClass('passive-alert');
	});

	/**
	 * @event module:app#app:app:loading
	 * @param {object} opts
	 */
	dispatcher.on('app:loading', function (opts = {}) {
		dispatcher.trigger('app:loading:show', {
			template: LoadingViewTemplate,
			model: new CoreModel({
				message: opts.message || '',
			}),
		});
	});

	/**
	 * @event module:app#app:app:loading:show
	 * @param {object} viewOptions options for the view
	 */
	dispatcher.on(
		'app:loading:show',
		function (viewOptions) {
			fsViews.add({
				viewClass: CoreView,
				showActions: ['app:token:loading:show'],
				viewOptions: viewOptions,
			});
		},
		window,
	);

	/**
	 * @event module:app#app:contact
	 * @fires module:app#app:contact:show
	 */
	dispatcher.off('app:contact');
	dispatcher.on('app:contact', function () {
		dispatcher.trigger('app:contact:show', {
			prompt: currentPortal.get('contactMessage'),
		});
	});
}); // app:extend

dispatcher.on('app:started', function () {
	var portal = currentPortal;
	// toggle watermarking
	// TODO: move this to the component
	Modernizr.addTest('watermark', function () {
		return currentPortal.get('watermark') > 0;
	});
	if (portal.get('watermark') > 0) {
		var url = `/theme/${portal.get('path')}/watermark?v=${randomChars(5)}`;
		$('head').append(
			`<style>
				html.watermark .asset:not([class*="custom-icon-"]) .thumbnail .thumbnail-holder:after, 
				html.watermark .preview-wrapper .preview:not([class*="custom-icon-"]):after, 
				html.watermark .original-size .original-holder:after,
				#asset-quickzoom .zoom-container:after, 
				html.watermark .watermark:after, 
				html.watermark .video-preview .video-js.vjs-fullscreen:before { 
					background-image: url("${url}")
				}
			</style>`,
		);
	}
	if (!_.isUndefined(portal.get('watermarkPos'))) {
		$('html').addClass('watermark-' + portal.get('watermarkPos'));
	}

	$('#wrapper-wrapper').show();
});
